import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  Users, 
  TrendingUp, 
  Phone, 
  Target,
  Flame,
  ThermometerSun,
  Snowflake,
  Activity,
  FolderKanban
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const STATUS_LABELS = {
  neu: 'Neu',
  kontaktiert: 'Kontaktiert',
  termin: 'Termin',
  pilot: 'Pilot',
  laufend: 'Laufend',
  abgelehnt: 'Abgelehnt'
};

const STATUS_COLORS = {
  neu: '#9CA3AF',
  kontaktiert: '#3B82F6',
  termin: '#8B5CF6',
  pilot: '#F59E0B',
  laufend: '#10B981',
  abgelehnt: '#EF4444'
};

const SCORE_COLORS = {
  heiss: '#E63946',
  mittel: '#F59E0B',
  kalt: '#3B82F6'
};

const ACTIVITY_TYPES = {
  call: { label: 'Anruf', color: 'text-green-600' },
  email: { label: 'E-Mail', color: 'text-blue-600' },
  meeting: { label: 'Meeting', color: 'text-purple-600' },
  note: { label: 'Notiz', color: 'text-gray-600' }
};

export const Dashboard = () => {
  const { get } = useApi();
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [loading, setLoading] = useState(true);

  const loadCampaigns = useCallback(async () => {
    try {
      const data = await get('/campaigns');
      setCampaigns(data);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    }
  }, [get]);

  const loadStats = useCallback(async () => {
    try {
      let endpoint = '/dashboard/stats';
      if (selectedCampaign && selectedCampaign !== 'all') {
        endpoint += `?campaign_id=${selectedCampaign}`;
      }
      const data = await get(endpoint);
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  }, [get, selectedCampaign]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const statusChartData = Object.entries(stats?.leads_by_status || {}).map(([key, value]) => ({
    name: STATUS_LABELS[key] || key,
    value,
    fill: STATUS_COLORS[key] || '#6B7280'
  }));

  const scoreChartData = Object.entries(stats?.leads_by_score || {}).map(([key, value]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    value,
    fill: SCORE_COLORS[key] || '#6B7280'
  }));

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <div className="flex items-center gap-4">
          {isAdmin && campaigns.length > 0 && (
            <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
              <SelectTrigger className="w-[200px]" data-testid="dashboard-campaign-filter">
                <FolderKanban className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Kampagne" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Kampagnen</SelectItem>
                {campaigns.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <span className="text-sm text-gray-500">
            Stand: {new Date().toLocaleDateString('de-DE')}
          </span>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
        <Card className="kpi-card" data-testid="kpi-total-leads">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Gesamt Leads</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.total_leads || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-gray-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card" data-testid="kpi-hot-leads">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Heiße Leads</p>
                <p className="text-3xl font-bold text-[#E63946]">{stats?.leads_by_score?.heiss || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <Flame className="h-6 w-6 text-[#E63946]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card" data-testid="kpi-activities-today">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Aktivitäten Heute</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.activities_today || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Phone className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card" data-testid="kpi-conversion-rate">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Konversionsrate</p>
                <p className="text-3xl font-bold text-green-600">{stats?.conversion_rate || 0}%</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Score Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="score-cards">
        <Card className="border-l-4 border-l-[#E63946]">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Flame className="h-8 w-8 text-[#E63946]" />
              <div>
                <p className="text-sm text-gray-500">Heiß</p>
                <p className="text-2xl font-bold">{stats?.leads_by_score?.heiss || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-400">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <ThermometerSun className="h-8 w-8 text-orange-400" />
              <div>
                <p className="text-sm text-gray-500">Mittel</p>
                <p className="text-2xl font-bold">{stats?.leads_by_score?.mittel || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Snowflake className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-sm text-gray-500">Kalt</p>
                <p className="text-2xl font-bold">{stats?.leads_by_score?.kalt || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="status-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Target className="h-5 w-5" />
              Leads nach Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={statusChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={100} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {statusChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="score-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Flame className="h-5 w-5" />
              Leads nach Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={scoreChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {scoreChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activities */}
      <Card data-testid="recent-activities">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Letzte Aktivitäten
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.recent_activities?.length > 0 ? (
            <div className="space-y-4">
              {stats.recent_activities.slice(0, 5).map((activity) => (
                <div 
                  key={activity.id} 
                  className="flex items-start gap-4 p-3 rounded-lg bg-gray-50"
                >
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    activity.typ === 'call' ? 'bg-green-500' :
                    activity.typ === 'email' ? 'bg-blue-500' :
                    activity.typ === 'meeting' ? 'bg-purple-500' : 'bg-gray-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${ACTIVITY_TYPES[activity.typ]?.color || 'text-gray-600'}`}>
                        {ACTIVITY_TYPES[activity.typ]?.label || activity.typ}
                      </span>
                      <span className="text-sm text-gray-400">•</span>
                      <span className="text-sm text-gray-500 font-mono">
                        {activity.lead_nummer}
                      </span>
                      <span className="text-sm font-medium text-gray-700 truncate">
                        {activity.lead_firma}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 truncate">{activity.beschreibung}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {activity.created_by_name} • {formatDate(activity.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Keine Aktivitäten vorhanden</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
